import type { TanStackDevtoolsReactInit } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import { createRootRouteWithContext, HeadContent, Scripts } from "@tanstack/react-router";
import type { ComponentType, CSSProperties } from "react";
import { lazy, Suspense, useEffect, useState } from "react";
import { ThemeProvider } from "../components/theme-provider";
import { Toaster } from "../components/ui/sonner";
import { usePostHogIdentify } from "../integrations/posthog/provider";
import TanStackQueryDevtools from "../integrations/tanstack-query/devtools";
import { ErrorBoundary } from "../lib/errors";
import { buildOrganization, buildWebSite, jsonLdScript } from "../lib/seo/json-ld";
import { buildLinks, buildMetaTags, getCanonical } from "../lib/seo/meta";
import { useAuthStore } from "../stores/auth-store";
import appCss from "../styles.css?url";

const isDev = import.meta.env.DEV;
const FAVICON_VERSION = "20260211";

type DevtoolsComponentType = ComponentType<TanStackDevtoolsReactInit>;
type RouterDevtoolsPanelProps = {
	style?: CSSProperties;
	className?: string;
	isOpen?: boolean;
	setIsOpen?: (isOpen: boolean) => void;
	handleDragStart?: (event: unknown) => void;
	router?: AnyRouter;
	shadowDOMTarget?: ShadowRoot;
};
type RouterDevtoolsPanelComponentType = ComponentType<RouterDevtoolsPanelProps>;

let Devtools: DevtoolsComponentType | null = null;
let RouterDevtoolsPanelComponent: RouterDevtoolsPanelComponentType | null = null;

if (isDev) {
	Devtools = lazy(() =>
		import("@tanstack/react-devtools").then((mod) => ({
			default: mod.TanStackDevtools,
		}))
	);

	RouterDevtoolsPanelComponent = lazy(() =>
		import("@tanstack/react-router-devtools").then((mod) => ({
			default: mod.TanStackRouterDevtoolsPanel,
		}))
	);
}

interface MyRouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	head: () => {
		// Default SEO meta tags - routes can override via their own head() exports
		const meta = buildMetaTags({
			// No title here - routes should provide their own
			// Falls back to defaultTitle from config
		});

		const links = [
			// Start TLS handshake with font CDN early
			{ rel: "preconnect", href: "https://cdn.jsdelivr.net", crossOrigin: "anonymous" as const },
			// Preload primary body font — eliminates CSS→parse→discover waterfall
			{
				rel: "preload",
				href: "https://cdn.jsdelivr.net/npm/geist@1.5.1/dist/fonts/geist-sans/Geist-Variable.woff2",
				as: "font",
				type: "font/woff2",
				crossOrigin: "anonymous" as const,
			},
			{
				rel: "stylesheet",
				href: appCss,
			},
			...buildLinks({
				canonical: getCanonical("/"),
			}),
		];

		// Structured data: Organization + WebSite (global, all pages)
		const organizationSchema = buildOrganization({
			logo: `${getCanonical("/")}/favicon.svg`,
			sameAs: ["https://x.com/m8tes_ai"],
		});
		const webSiteSchema = buildWebSite();

		return {
			meta,
			links,
			scripts: [jsonLdScript(organizationSchema), jsonLdScript(webSiteSchema)],
		};
	},

	shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
	const user = useAuthStore((state) => state.user);
	const [mounted, setMounted] = useState(false);

	// Identify user in PostHog when auth state changes
	usePostHogIdentify(user);

	useEffect(() => {
		setMounted(true);
	}, []);

	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				<link
					rel="icon"
					href={`/favicon.svg?v=${FAVICON_VERSION}`}
					type="image/svg+xml"
					sizes="any"
				/>
				<link
					rel="icon"
					href={`/favicon-32x32.png?v=${FAVICON_VERSION}`}
					type="image/png"
					sizes="32x32"
				/>
				<link
					rel="icon"
					href={`/favicon-16x16.png?v=${FAVICON_VERSION}`}
					type="image/png"
					sizes="16x16"
				/>
				<link rel="icon" href={`/favicon.ico?v=${FAVICON_VERSION}`} sizes="any" />
				<link rel="shortcut icon" href={`/favicon.ico?v=${FAVICON_VERSION}`} />
				<link
					rel="apple-touch-icon"
					href={`/apple-touch-icon.png?v=${FAVICON_VERSION}`}
					sizes="180x180"
				/>
				<link rel="manifest" href={`/manifest.json?v=${FAVICON_VERSION}`} />
				<HeadContent />
			</head>
			<body>
				<ThemeProvider attribute="class" defaultTheme="system" enableSystem>
					<ErrorBoundary>
						{children}
						{mounted && <Toaster position="top-right" />}
						{isDev && Devtools && (
							<Suspense fallback={null}>
								<Devtools
									config={{
										position: "bottom-right",
									}}
									plugins={[
										...(RouterDevtoolsPanelComponent
											? [
													{
														name: "Tanstack Router",
														render: (
															<Suspense fallback={null}>
																<RouterDevtoolsPanelComponent />
															</Suspense>
														),
													},
												]
											: []),
										TanStackQueryDevtools,
									]}
								/>
							</Suspense>
						)}
					</ErrorBoundary>
				</ThemeProvider>
				<Scripts />
			</body>
		</html>
	);
}
